/**
 * Rapportage-only "branches": merge multiple client (bedrijf) or recruiter profile ids
 * under one label. Stored in localStorage per Werftools-tenant (user.clientId).
 */

export type ReportingBranchKind = 'clients' | 'recruiters';

export type ReportingBranch = {
  id: string;
  name: string;
  kind: ReportingBranchKind;
  memberIds: string[];
};

const LEGACY_KEY = 'werftools_client_sets';
const EVENT = 'werftools-reporting-branches-saved';

function storageKey(tenantId: string | null | undefined): string {
  return `werftools_reporting_branches_v1_${tenantId || 'global'}`;
}

function genId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function migrationDoneKey(tenantId: string | null | undefined): string {
  return `werftools_reporting_branches_legacy_migrated_${tenantId || 'global'}`;
}

export function loadReportingBranches(tenantId: string | null | undefined): ReportingBranch[] {
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (raw) {
      const parsed = JSON.parse(raw) as ReportingBranch[];
      if (Array.isArray(parsed) && parsed.length) return parsed.filter(validBranch);
    }
  } catch {
    /* ignore */
  }
  if (!localStorage.getItem(migrationDoneKey(tenantId))) {
    const migrated = migrateLegacyClientSets(tenantId);
    if (migrated.length) return migrated;
  }
  return [];
}

function validBranch(b: unknown): b is ReportingBranch {
  if (!b || typeof b !== 'object') return false;
  const o = b as ReportingBranch;
  return (
    typeof o.id === 'string' &&
    typeof o.name === 'string' &&
    (o.kind === 'clients' || o.kind === 'recruiters') &&
    Array.isArray(o.memberIds)
  );
}

/** One-time read from pre-branch `werftools_client_sets` → branches (clients). */
export function migrateLegacyClientSets(tenantId: string | null | undefined): ReportingBranch[] {
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (!legacy) {
      localStorage.setItem(migrationDoneKey(tenantId), '1');
      return [];
    }
    const arr = JSON.parse(legacy) as { id: string; name: string; clientIds: string[] }[];
    if (!Array.isArray(arr) || arr.length === 0) {
      localStorage.setItem(migrationDoneKey(tenantId), '1');
      return [];
    }
    const branches: ReportingBranch[] = arr
      .filter((x) => x && x.name && Array.isArray(x.clientIds))
      .map((x) => ({
        id: x.id || genId(),
        name: x.name,
        kind: 'clients' as const,
        memberIds: x.clientIds,
      }));
    if (branches.length) {
      saveReportingBranches(tenantId, branches);
    }
    localStorage.setItem(migrationDoneKey(tenantId), '1');
    return branches;
  } catch {
    return [];
  }
}

export function saveReportingBranches(tenantId: string | null | undefined, branches: ReportingBranch[]): void {
  localStorage.setItem(storageKey(tenantId), JSON.stringify(branches));
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function addReportingBranch(
  tenantId: string | null | undefined,
  partial: Omit<ReportingBranch, 'id'>
): ReportingBranch {
  const b: ReportingBranch = { ...partial, id: genId() };
  const all = loadReportingBranches(tenantId);
  all.push(b);
  saveReportingBranches(tenantId, all);
  return b;
}

export function updateReportingBranch(
  tenantId: string | null | undefined,
  id: string,
  patch: Partial<Pick<ReportingBranch, 'name' | 'kind' | 'memberIds'>>
): void {
  const all = loadReportingBranches(tenantId);
  const i = all.findIndex((x) => x.id === id);
  if (i < 0) return;
  all[i] = { ...all[i], ...patch };
  saveReportingBranches(tenantId, all);
}

export function deleteReportingBranch(tenantId: string | null | undefined, id: string): void {
  saveReportingBranches(
    tenantId,
    loadReportingBranches(tenantId).filter((b) => b.id !== id)
  );
}

export function reportingBranchesStorageEvent(): string {
  return EVENT;
}
